import React, { useState, useEffect, useRef } from "react";
import {
	Card,
	CardBody,
	CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
	Button,
	TextField,
	MuiThemeProvider,
	createMuiTheme,
	FormControlLabel,
	Switch,
	Typography,
	Divider,
	MenuItem,
	Checkbox,
	ListItemText,
	FormControl,
	InputLabel,
	Select,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import { useHistory, useParams } from "react-router-dom";
import {
	deletePromoPortfolioProject,
	getPromoPortfolioProjectById,
	postPromoPortfolioProject,
	putPromoPortfolioProject,
} from "../../../../api/promo-portfolio-project";
import { getPromoPortfolioSectorsAdmin } from "../../../../api/promo-portfolio-sector";
import { getPromoClientsAdmin } from "../../../../api/promo-client";
import { uploadSingleFile } from "../../../../api/files";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { SERVER_URL } from "../../../../utils";

const theme = createMuiTheme({
	palette: {
		secondary: {
			main: "#F64E60",
		},
	},
});

const LOCALE_LABELS = [
	{ key: "es", label: "Español" },
	{ key: "ca", label: "Catalán" },
	{ key: "en", label: "Inglés" },
];

function emptyLocaleBlock() {
	return {
		title: "",
		excerpt: "",
		description: "",
		metaTitle: "",
		metaDescription: "",
	};
}

function getEmptyProject() {
	return {
		key: "",
		client: "",
		sectorIds: [],
		featured: false,
		active: true,
		slugs: { es: "", ca: "", en: "" },
		locales: {
			es: emptyLocaleBlock(),
			ca: emptyLocaleBlock(),
			en: emptyLocaleBlock(),
		},
		media: [],
	};
}

function resolveMediaUrl(raw) {
	const p = String(raw || "").trim();
	if (!p) return "";
	if (/^https?:\/\//i.test(p) || p.startsWith("data:")) return p;
	if (p.startsWith("/")) return `${String(SERVER_URL || "").replace(/\/$/, "")}${p}`;
	return `${String(SERVER_URL || "").replace(/\/$/, "")}/${p.replace(/^\/+/, "")}`;
}

function LocaleFields({ label, localeKey, project, onChange }) {
	const block = project.locales?.[localeKey] || emptyLocaleBlock();
	return (
		<>
			<Typography variant="subtitle1" style={{ fontWeight: 700, marginTop: 8 }}>
				{label}
			</Typography>
			<TextField
				label="Título"
				value={block.title}
				onChange={onChange(localeKey, "title")}
				InputLabelProps={{ shrink: true }}
				margin="normal"
				variant="outlined"
				fullWidth
				required={localeKey === "es"}
			/>
			<TextField
				label="Extracto (tarjeta)"
				value={block.excerpt}
				onChange={onChange(localeKey, "excerpt")}
				InputLabelProps={{ shrink: true }}
				margin="normal"
				variant="outlined"
				fullWidth
				multiline
				rows={3}
			/>
			<TextField
				label="Descripción (página detalle)"
				value={block.description}
				onChange={onChange(localeKey, "description")}
				InputLabelProps={{ shrink: true }}
				margin="normal"
				variant="outlined"
				fullWidth
				multiline
				rows={12}
			/>
			<TextField
				label="Meta título (SEO)"
				value={block.metaTitle}
				onChange={onChange(localeKey, "metaTitle")}
				InputLabelProps={{ shrink: true }}
				margin="normal"
				variant="outlined"
				fullWidth
			/>
			<TextField
				label="Meta descripción (SEO)"
				value={block.metaDescription}
				onChange={onChange(localeKey, "metaDescription")}
				InputLabelProps={{ shrink: true }}
				margin="normal"
				variant="outlined"
				fullWidth
				multiline
				rows={3}
			/>
			<Divider style={{ marginTop: 16 }} />
		</>
	);
}

export default function EditPromoPortfolioProjectPage() {
	const projectId = useParams().id;
	const history = useHistory();
	const imageInputRef = useRef(null);
	const videoInputRef = useRef(null);
	const [project, setProject] = useState(getEmptyProject());
	const [sectors, setSectors] = useState([]);
	const [promoClients, setPromoClients] = useState([]);
	const [uploading, setUploading] = useState(false);
	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
	const {
		isLoading: isLoadingData,
		disableLoading: disableLoadingData,
		ContentSkeleton,
	} = useSkeleton();

	useEffect(() => {
		let mounted = true;
		getPromoPortfolioSectorsAdmin()
			.then((res) => {
				if (!mounted) return;
				if (res.status === 200) setSectors(res.data || []);
			})
			.catch(() => {});
		getPromoClientsAdmin()
			.then((res) => {
				if (!mounted) return;
				if (res.status === 200) {
					const list = (res.data || [])
						.filter((c) => c.active !== false)
						.sort((a, b) => String(a.name).localeCompare(String(b.name), "es"));
					setPromoClients(list);
				}
			})
			.catch(() => {});
		return () => {
			mounted = false;
		};
	}, []);

	useEffect(() => {
		let mounted = true;
		if (!projectId) {
			disableLoadingData();
			return () => {
				mounted = false;
			};
		}
		getPromoPortfolioProjectById(projectId)
			.then((res) => {
				if (!mounted) return;
				if (res.status === 200) {
					const p = res.data || {};
					setProject({
						key: p.key || "",
						client: p.client || "",
						sectorIds: (p.sectorIds || []).map((s) => String(s?._id || s)),
						featured: Boolean(p.featured),
						active: p.active !== false,
						slugs: {
							es: p.slugs?.es || "",
							ca: p.slugs?.ca || "",
							en: p.slugs?.en || "",
						},
						locales: {
							es: { ...emptyLocaleBlock(), ...(p.locales?.es || {}) },
							ca: { ...emptyLocaleBlock(), ...(p.locales?.ca || {}) },
							en: { ...emptyLocaleBlock(), ...(p.locales?.en || {}) },
						},
						media: Array.isArray(p.media) ? p.media : [],
					});
					disableLoadingData();
				}
			})
			.catch((error) => {
				if (!mounted) return;
				alertError({ error, customMessage: "No se pudo obtener el proyecto." });
				history.push("/promo-portfolio-projects");
			});
		return () => {
			mounted = false;
		};
	}, [projectId, disableLoadingData, history]);

	const handleLocaleChange = (localeKey, field) => (e) => {
		const value = e.target.value;
		setProject((prev) => ({
			...prev,
			locales: {
				...prev.locales,
				[localeKey]: {
					...(prev.locales?.[localeKey] || {}),
					[field]: value,
				},
			},
		}));
	};

	const handleSlugChange = (localeKey) => (e) => {
		const value = e.target.value;
		setProject((prev) => ({
			...prev,
			slugs: { ...prev.slugs, [localeKey]: value },
		}));
	};

	async function handleMediaUpload(e, type) {
		const file = e.target.files?.[0];
		e.target.value = "";
		if (!file) return;
		setUploading(true);
		try {
			const res = await uploadSingleFile(file, "promo-portfolio");
			if (res?.data?.fileURL) {
				setProject((prev) => ({
					...prev,
					media: [
						...(prev.media || []),
						{ type, url: res.data.fileURL, sortOrder: (prev.media || []).length },
					],
				}));
				alertSuccess({ title: "Archivo subido", customMessage: "Medio añadido a la galería." });
			}
		} catch (error) {
			alertError({ error, customMessage: "No se pudo subir el archivo." });
		} finally {
			setUploading(false);
		}
	}

	function moveMedia(index, direction) {
		setProject((prev) => {
			const list = [...(prev.media || [])];
			const swap = direction === "up" ? index - 1 : index + 1;
			if (swap < 0 || swap >= list.length) return prev;
			const tmp = list[index];
			list[index] = list[swap];
			list[swap] = tmp;
			return {
				...prev,
				media: list.map((item, i) => ({ ...item, sortOrder: i })),
			};
		});
	}

	function removeMedia(index) {
		setProject((prev) => ({
			...prev,
			media: (prev.media || [])
				.filter((_, i) => i !== index)
				.map((item, i) => ({ ...item, sortOrder: i })),
		}));
	}

	function saveProject() {
		const body = {
			key: String(project.key || "").trim(),
			client: String(project.client || "").trim(),
			sectorIds: project.sectorIds || [],
			featured: !!project.featured,
			active: !!project.active,
			slugs: project.slugs,
			locales: project.locales,
			media: project.media || [],
		};

		if (!body.key) {
			alertError({ customMessage: "La clave interna es obligatoria." });
			return;
		}
		if (!body.sectorIds.length) {
			alertError({ customMessage: "Selecciona al menos un sector." });
			return;
		}
		if (!body.locales?.es?.title) {
			alertError({ customMessage: "El título en español es obligatorio." });
			return;
		}

		if (!projectId) {
			postPromoPortfolioProject(body)
				.then((res) => {
					if (res.status === 201) {
						alertSuccess({ title: "Guardado", customMessage: "Proyecto creado correctamente." });
						history.push("/promo-portfolio-projects");
					}
				})
				.catch((error) => {
					alertError({ error, customMessage: "No se pudo crear el proyecto." });
				});
		} else {
			putPromoPortfolioProject(projectId, body)
				.then((res) => {
					if (res.status === 200) {
						alertSuccess({ title: "Guardado", customMessage: "Cambios guardados correctamente." });
						history.push("/promo-portfolio-projects");
					}
				})
				.catch((error) => {
					alertError({ error, customMessage: "No se pudieron guardar los cambios." });
				});
		}
	}

	if (isLoadingData) return <ContentSkeleton />;

	const activeSectors = sectors.filter((s) => s.active !== false);

	return (
		<>
			<Card>
				<CardHeader title={projectId ? "Editar proyecto portfolio" : "Nuevo proyecto portfolio"} />
				<CardBody>
					<TextField
						label="Clave interna"
						helperText="Identificador único (ej: onconext). No cambiar si el proyecto ya está publicado."
						value={project.key}
						onChange={(e) => setProject({ ...project, key: e.target.value })}
						InputLabelProps={{ shrink: true }}
						margin="normal"
						variant="outlined"
						fullWidth
						required
					/>
					<FormControl variant="outlined" fullWidth margin="normal">
						<InputLabel shrink>Cliente (opcional)</InputLabel>
						<Select
							value={project.client || ""}
							onChange={(e) => setProject({ ...project, client: e.target.value })}
							displayEmpty
							label="Cliente (opcional)"
						>
							<MenuItem value="">
								<em>Sin cliente</em>
							</MenuItem>
							{project.client &&
								!promoClients.some((c) => c.name === project.client) && (
									<MenuItem value={project.client}>
										{project.client} (actual, no en clientes web)
									</MenuItem>
								)}
							{promoClients.map((client) => (
								<MenuItem key={client._id} value={client.name}>
									{client.name}
								</MenuItem>
							))}
						</Select>
						<Typography variant="caption" color="textSecondary" style={{ marginTop: 8, display: "block" }}>
							Lista desde «Clientes web». El logo se mostrará en evenely.com si el nombre coincide.
						</Typography>
					</FormControl>
					<FormControl variant="outlined" fullWidth margin="normal">
						<InputLabel shrink>Sectores</InputLabel>
						<Select
							multiple
							value={project.sectorIds}
							onChange={(e) => setProject({ ...project, sectorIds: e.target.value })}
							renderValue={(selected) =>
								activeSectors
									.filter((s) => selected.includes(String(s._id)))
									.map((s) => s.name)
									.join(", ")
							}
							label="Sectores"
						>
							{activeSectors.map((sector) => (
								<MenuItem key={sector._id} value={String(sector._id)}>
									<Checkbox checked={project.sectorIds.includes(String(sector._id))} color="primary" />
									<ListItemText primary={sector.name} />
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</CardBody>
			</Card>

			<Card>
				<CardHeader title="URLs (slug por idioma)" />
				<CardBody>
					{LOCALE_LABELS.map(({ key, label }) => (
						<TextField
							key={key}
							label={`Slug ${label}`}
							helperText={key === "es" ? "Ej: onconext → /portfolio/onconext" : ""}
							value={project.slugs?.[key] || ""}
							onChange={handleSlugChange(key)}
							InputLabelProps={{ shrink: true }}
							margin="normal"
							variant="outlined"
							fullWidth
						/>
					))}
				</CardBody>
			</Card>

			<Card>
				<CardHeader title="Contenido por idioma" />
				<CardBody>
					{LOCALE_LABELS.map(({ key, label }) => (
						<LocaleFields
							key={key}
							label={label}
							localeKey={key}
							project={project}
							onChange={handleLocaleChange}
						/>
					))}
				</CardBody>
			</Card>

			<Card>
				<CardHeader title="Galería (fotos y vídeos)" />
				<CardBody>
					<input
						ref={imageInputRef}
						type="file"
						accept="image/*"
						style={{ display: "none" }}
						onChange={(e) => handleMediaUpload(e, "image")}
					/>
					<input
						ref={videoInputRef}
						type="file"
						accept="video/mp4,video/webm"
						style={{ display: "none" }}
						onChange={(e) => handleMediaUpload(e, "video")}
					/>
					<Button
						variant="outlined"
						color="primary"
						disabled={uploading}
						onClick={() => imageInputRef.current?.click()}
						style={{ marginRight: 8 }}
					>
						Subir imagen
					</Button>
					<Button
						variant="outlined"
						color="primary"
						disabled={uploading}
						onClick={() => videoInputRef.current?.click()}
					>
						Subir vídeo
					</Button>

					{(project.media || []).length === 0 ? (
						<Typography color="textSecondary" style={{ marginTop: 16 }}>
							Sin medios. Sube capturas o vídeos del proyecto.
						</Typography>
					) : (
						<div style={{ marginTop: 16 }}>
							{(project.media || []).map((item, index) => {
								const src = resolveMediaUrl(item.url);
								const isFirst = index === 0;
								const isLast = index === project.media.length - 1;
								return (
									<div
										key={`${item.url}-${index}`}
										style={{
											display: "flex",
											alignItems: "center",
											gap: 12,
											marginBottom: 12,
											padding: 12,
											border: "1px solid #eee",
											borderRadius: 8,
										}}
									>
										<div style={{ width: 120, flexShrink: 0 }}>
											{item.type === "video" ? (
												<video src={src} style={{ maxWidth: 120, maxHeight: 72 }} muted />
											) : (
												<img
													src={src}
													alt=""
													style={{ maxWidth: 120, maxHeight: 72, objectFit: "cover", borderRadius: 4 }}
												/>
											)}
										</div>
										<div style={{ flex: 1, fontSize: 13 }}>
											<strong>{item.type === "video" ? "Vídeo" : "Imagen"}</strong>
											<div style={{ color: "#888", wordBreak: "break-all" }}>{item.url}</div>
										</div>
										<Button size="small" disabled={isFirst} onClick={() => moveMedia(index, "up")}>
											<ArrowUpwardIcon fontSize="small" />
										</Button>
										<Button size="small" disabled={isLast} onClick={() => moveMedia(index, "down")}>
											<ArrowDownwardIcon fontSize="small" />
										</Button>
										<Button size="small" color="secondary" onClick={() => removeMedia(index)}>
											<DeleteIcon fontSize="small" />
										</Button>
									</div>
								);
							})}
						</div>
					)}
				</CardBody>
			</Card>

			<Card>
				<CardBody>
					<FormControlLabel
						control={
							<Switch
								checked={!!project.featured}
								onChange={(e) => setProject({ ...project, featured: e.target.checked })}
								color="primary"
							/>
						}
						label="Destacado"
					/>
					<FormControlLabel
						control={
							<Switch
								checked={!!project.active}
								onChange={(e) => setProject({ ...project, active: e.target.checked })}
								color="primary"
							/>
						}
						label={project.active ? "Activo (visible en evenely.com)" : "Inactivo (oculto en la web)"}
					/>
				</CardBody>
			</Card>

			<Button
				onClick={() => history.push("/promo-portfolio-projects")}
				variant="outlined"
				style={{ marginRight: 20 }}
			>
				Volver
			</Button>
			<Button onClick={saveProject} variant="outlined" color="primary" style={{ marginRight: 20 }}>
				Guardar proyecto
			</Button>
			{projectId && (
				<>
					<MuiThemeProvider theme={theme}>
						<Button onClick={() => setOpenConfirmDialog(true)} variant="outlined" color="secondary">
							Eliminar proyecto
						</Button>
					</MuiThemeProvider>

					<ConfirmDialog
						title="¿Seguro que quieres eliminar este proyecto?"
						open={openConfirmDialog}
						setOpen={setOpenConfirmDialog}
						onConfirm={() => {
							deletePromoPortfolioProject(projectId)
								.then((res) => {
									if (res.status === 204) {
										alertSuccess({ title: "Eliminado", customMessage: "Proyecto eliminado correctamente." });
										history.push("/promo-portfolio-projects");
									}
								})
								.catch((error) => {
									alertError({ error, customMessage: "No se pudo eliminar el proyecto." });
								});
						}}
					/>
				</>
			)}
		</>
	);
}
